import {
  ChatUserGroupRole,
  RequireOnlyOne,
  WorkflowUserGroupRole
} from '@kindo/universal';

import { nextTrpc } from '~/trpc';

interface UserGroupRole {
  id: string;
  name: string;
  role: WorkflowUserGroupRole | ChatUserGroupRole | string;
}

interface UserGroupShareOption {
  count: number;
  id: string;
  isUserGroupMember: boolean;
  name: string;
}

interface UseShareWithUserGroupArgs {
  chatId: string;
  existingUserGroupRoles: UserGroupRole[];
  workflowId: string;
}

interface UseShareWithUserGroupReturnObject {
  availableUserGroupShareOptions: UserGroupShareOption[];
  tooltip: string;
  usersUserGroups: UserGroupShareOption[];
}

const useUserGroupShareOptions = ({
  chatId,
  workflowId,
  existingUserGroupRoles
}: RequireOnlyOne<
  UseShareWithUserGroupArgs,
  'chatId' | 'workflowId'
>): UseShareWithUserGroupReturnObject => {
  const { data: usersUserGroups } = nextTrpc.userGroups.list.useQuery();

  const getUserGroupShareOptions = () => {
    if (workflowId) {
      return nextTrpc.workflows.getWorkflowUserGroupShareOptions.useQuery({
        workflowId
      });
    }
    if (chatId) {
      return nextTrpc.chats.getChatUserGroupShareOptions.useQuery({ chatId });
    }
    throw new Error('Either workflowId or chatId required');
  };

  const allUserGroupShareOptions = getUserGroupShareOptions().data?.items ?? [];
  const availableUserGroupShareOptions = allUserGroupShareOptions.filter(
    (group) => !existingUserGroupRoles.some((role) => role.id === group.id)
  );
  const getUserGroupDisabledTooltip = () => {
    if (allUserGroupShareOptions.length === 0) {
      return 'No user groups available';
    }
    if (availableUserGroupShareOptions.length === 0) {
      return 'All possible user groups are already added';
    }
    return '';
  };

  return {
    usersUserGroups: usersUserGroups?.items ?? [],
    availableUserGroupShareOptions,
    tooltip: getUserGroupDisabledTooltip()
  };
};

export default useUserGroupShareOptions;
